import { create } from 'zustand';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { errorMessage } from '../i18n/errorMessages';
import type { Channel, Zone } from '../types';
import * as api from '../lib/api';
import { resolveDefaultChannel } from '../lib/defaultChannel';
import { useAuthStore } from './useAuthStore';

interface ChannelState {
  channels: Channel[];
  zones: Zone[];
  loading: boolean;
  loadError: MessageDescriptor[] | null;
  currentChannelId: string | null;
  channelMembersVersion: Record<string, number>;
  setChannels: (channels: Channel[]) => void;
  setSnapshot: (snapshot: api.GuildChannelSnapshot, guildId?: string) => void;
  addChannel: (channel: Channel) => void;
  removeChannel: (channelId: string) => void;
  updateChannel: (channelId: string, updates: Partial<Channel>) => void;
  setCurrentChannel: (channelId: string | null) => void;
  updateUnread: (channelId: string, count: number) => void;
  clearUnread: (channelId: string) => void;
  bumpChannelMembersVersion: (channelId: string) => void;
  navigateAfterLeave: (channelId: string) => void;
  purgeSubtree: (channelIds: string[]) => void;
  reset: () => void;
  loadChannels: (guildId?: string) => Promise<void>;
  invalidateChannels: (guildId?: string) => Promise<void>;
  selectChannel: (channelId: string) => void;
  createChannel: (
    name: string,
    parentId: string | null,
    topic?: string,
    memberIds?: string[],
    visibility?: 'public' | 'private',
  ) => Promise<Channel>;
}

interface ChannelScope {
  accountId: string | null;
  guildId: string;
  desiredRevision: number;
  dirty: boolean;
  structuralError: unknown;
  permissionError: unknown;
  permissionRefreshRequired: boolean;
  permissionFlight?: Promise<void>;
  flight?: { controller: AbortController; promise: Promise<void> };
}

const scopes = new Map<string, ChannelScope>();
const stagedControllers = new Set<AbortController>();
let contextGeneration = 0;
let streamRevision = 0;
const streamRevisions = new Map<string, { unread?: number; message?: number }>();

const accountId = () => useAuthStore.getState().currentUser?.id ?? null;
const scopeFor = (guildId: string): ChannelScope => {
  let scope = scopes.get(guildId);
  if (!scope || scope.accountId !== accountId()) {
    scope = {
      accountId: accountId(),
      guildId,
      desiredRevision: 0,
      dirty: false,
      structuralError: null,
      permissionError: null,
      permissionRefreshRequired: false,
    };
    scopes.set(guildId, scope);
  }
  return scope;
};

const scopeCurrent = (scope: ChannelScope) =>
  scopes.get(scope.guildId) === scope && scope.accountId === accountId();

/** Async work must belong to the same account and committed Server generation. */
export const captureChannelContext = (): (() => boolean) => {
  const generation = contextGeneration;
  const account = accountId();
  const guildId = useAuthStore.getState().guildId;
  return () =>
    generation === contextGeneration &&
    account === accountId() &&
    guildId === useAuthStore.getState().guildId;
};

export const captureChannelRevision = (): (() => boolean) => {
  const sameContext = captureChannelContext();
  const guildId = useAuthStore.getState().guildId;
  const scope = guildId ? scopeFor(guildId) : null;
  const revision = scope?.desiredRevision;
  return () => sameContext() && scope?.desiredRevision === revision;
};

/** Joins an externally requested read without creating another invalidation. */
export const waitForChannelSnapshot = async (): Promise<boolean> => {
  const sameContext = captureChannelContext();
  const guildId = useAuthStore.getState().guildId;
  const scope = guildId ? scopeFor(guildId) : null;
  while (sameContext() && scope?.flight) await scope.flight.promise;
  return (
    sameContext() && !useChannelStore.getState().loading && !useChannelStore.getState().loadError
  );
};

export const invalidateChannelScopes = (): Promise<void> => {
  const guildIds = new Set(scopes.keys());
  const activeGuildId = useAuthStore.getState().guildId;
  if (activeGuildId) guildIds.add(activeGuildId);
  return Promise.all(
    [...guildIds].map((guildId) => useChannelStore.getState().invalidateChannels(guildId)),
  ).then(() => {});
};

const cancelReads = () => {
  contextGeneration++;
  for (const controller of stagedControllers) controller.abort();
  for (const scope of scopes.values()) {
    scope.flight?.controller.abort();
    scope.flight = undefined;
    scope.permissionFlight = undefined;
    scope.dirty = false;
  }
};

useAuthStore.subscribe((state, previous) => {
  if (state.guildId !== previous.guildId || state.currentUser?.id !== previous.currentUser?.id) {
    cancelReads();
    if (state.currentUser?.id !== previous.currentUser?.id) scopes.clear();
  }
});

/** A named child is publishable only with its actual parent from the same read. */
export const validateChannelSnapshot = (
  snapshot: api.GuildChannelSnapshot,
  guildId?: string,
): void => {
  if (!Array.isArray(snapshot.channels) || !Array.isArray(snapshot.zones)) {
    throw new Error('The Project channel snapshot is incomplete.');
  }
  const zoneIds = new Set<string>();
  for (const zone of snapshot.zones) {
    if (
      typeof zone.id !== 'string' ||
      !zone.id ||
      typeof zone.name !== 'string' ||
      !zone.name ||
      zoneIds.has(zone.id) ||
      (guildId && zone.guild_id !== undefined && zone.guild_id !== guildId)
    )
      throw new Error('The Project Zone snapshot is invalid.');
    zoneIds.add(zone.id);
  }
  const channelIds = new Set<string>();
  for (const channel of snapshot.channels) {
    if (
      typeof channel.id !== 'string' ||
      !channel.id ||
      channelIds.has(channel.id) ||
      zoneIds.has(channel.id) ||
      channel.channel_type !== 0 ||
      (guildId && channel.guild_id !== guildId) ||
      (channel.parent_id !== null &&
        (typeof channel.parent_id !== 'string' || !zoneIds.has(channel.parent_id)))
    )
      throw new Error('The Project channel placement snapshot is invalid.');
    channelIds.add(channel.id);
  }
};

/** Staging includes companion reads, so hints received while they wait still fence publication. */
export const commitServerChannelSnapshot = async <T>(
  guildId: string,
  readCompanion: (signal: AbortSignal) => Promise<T>,
  commit: (snapshot: api.GuildChannelSnapshot, companion: T) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const scope = scopeFor(guildId);
  const generation = contextGeneration;
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  stagedControllers.add(controller);
  try {
    while (scopeCurrent(scope) && generation === contextGeneration && !controller.signal.aborted) {
      const capturedRevision = scope.desiredRevision;
      try {
        const [snapshot, context] = await Promise.all([
          api.fetchGuildChannels(guildId, controller.signal),
          readCompanion(controller.signal),
        ]);
        if (!scopeCurrent(scope) || generation !== contextGeneration || controller.signal.aborted)
          return;
        if (capturedRevision !== scope.desiredRevision) continue;
        validateChannelSnapshot(snapshot, guildId);
        commit(snapshot, context);
        return;
      } catch (error) {
        if (!scopeCurrent(scope) || generation !== contextGeneration || controller.signal.aborted)
          return;
        if (capturedRevision !== scope.desiredRevision) continue;
        throw error;
      }
    }
  } finally {
    stagedControllers.delete(controller);
    signal?.removeEventListener('abort', abort);
  }
};

const replacementChannelId = (channels: Channel[]): string | null =>
  resolveDefaultChannel(channels, useAuthStore.getState().guildId, 'main')?.id ?? null;

const parentKnown = (channel: Channel, zones: Zone[]) =>
  channel.channel_type === 0 &&
  (channel.parent_id === null || zones.some((zone) => zone.id === channel.parent_id));

const refreshStructure = (guildId?: string) => {
  // The store exposes the failure to the UI; a background hint owns no retry policy.
  void useChannelStore
    .getState()
    .invalidateChannels(guildId)
    .catch(() => {});
};

const markStream = (channelId: string, field: 'unread' | 'message') => {
  const versions = streamRevisions.get(channelId) ?? {};
  versions[field] = ++streamRevision;
  streamRevisions.set(channelId, versions);
};

const scopeLoadError = (scope: ChannelScope): MessageDescriptor[] | null => {
  const errors: MessageDescriptor[] = [];
  if (scope.structuralError)
    errors.push(errorMessage(scope.structuralError, msg`Could not refresh channels and Zones.`));
  if (scope.permissionError) {
    const context = msg`Channel created, but its permissions could not be refreshed.`;
    errors.push(context);
    const detail = errorMessage(scope.permissionError, context);
    if (detail !== context) errors.push(detail);
  }
  return errors.length ? errors : null;
};

const refreshPermissions = (scope: ChannelScope): Promise<void> => {
  if (scope.permissionFlight) return scope.permissionFlight;
  const sameContext = captureChannelContext();
  const isCurrent = () =>
    sameContext() && scopeCurrent(scope) && scope.guildId === useAuthStore.getState().guildId;
  scope.permissionRefreshRequired = true;
  const promise = api
    .fetchMyPermissions()
    .then((permissions) => {
      if (!isCurrent()) return;
      useAuthStore.getState().setPermissions(permissions.details);
      scope.permissionRefreshRequired = false;
      scope.permissionError = null;
      useChannelStore.setState({ loadError: scopeLoadError(scope) });
    })
    .catch((error: unknown) => {
      if (isCurrent()) {
        scope.permissionError = error;
        useChannelStore.setState({ loadError: scopeLoadError(scope) });
      }
      throw error;
    })
    .finally(() => {
      if (scope.permissionFlight === promise) scope.permissionFlight = undefined;
    });
  scope.permissionFlight = promise;
  return promise;
};

export const useChannelStore = create<ChannelState>()((set, get) => ({
  channels: [],
  zones: [],
  loading: false,
  loadError: null,
  currentChannelId: null,
  channelMembersVersion: {},

  setChannels: (channels) => get().setSnapshot({ channels, zones: get().zones }),

  setSnapshot: (snapshot, guildId) => {
    validateChannelSnapshot(snapshot);
    cancelReads();
    streamRevisions.clear();
    const targetGuildId = guildId ?? useAuthStore.getState().guildId;
    const scope = targetGuildId ? scopeFor(targetGuildId) : null;
    if (scope) scope.structuralError = null;
    set({ ...snapshot, loading: false, loadError: scope ? scopeLoadError(scope) : null });
  },

  addChannel: (channel) => {
    if (channel.guild_id !== useAuthStore.getState().guildId) {
      if (channel.guild_id) refreshStructure(channel.guild_id);
      return;
    }
    set((state) => {
      if (
        !parentKnown(channel, state.zones) ||
        state.channels.some((row) => row.id === channel.id)
      ) {
        return state;
      }
      return { channels: [...state.channels, channel] };
    });
    refreshStructure(channel.guild_id);
  },

  removeChannel: (channelId) => {
    set((state) => {
      const channels = state.channels.filter((channel) => channel.id !== channelId);
      return {
        channels,
        currentChannelId:
          state.currentChannelId === channelId
            ? replacementChannelId(channels)
            : state.currentChannelId,
      };
    });
    refreshStructure();
  },

  updateChannel: (channelId, updates) => {
    const structural = Object.keys(updates).some(
      (key) => key !== 'unread_count' && key !== 'last_message_at',
    );
    if (!structural) {
      if ('unread_count' in updates) markStream(channelId, 'unread');
      if ('last_message_at' in updates) markStream(channelId, 'message');
    }
    set((state) => ({
      channels: state.channels.map((channel) => {
        if (channel.id !== channelId) return channel;
        if (!structural) {
          const latest = updates.last_message_at;
          return {
            ...channel,
            ...updates,
            ...('last_message_at' in updates
              ? { last_message_at: Math.max(channel.last_message_at ?? 0, latest ?? 0) || null }
              : {}),
          };
        }
        // Structural frames carry historical stream fields, not stream authority.
        const { unread_count: _unread, last_message_at: _message, ...metadata } = updates;
        const updated = { ...channel, ...metadata };
        return parentKnown(updated, state.zones) ? updated : channel;
      }),
    }));
    if (structural) refreshStructure();
  },

  setCurrentChannel: (channelId) => set({ currentChannelId: channelId }),
  updateUnread: (channelId, count) => get().updateChannel(channelId, { unread_count: count }),
  clearUnread: (channelId) => {
    const channel = get().channels.find((row) => row.id === channelId);
    if (channel?.channel_type === 0 && channel.is_member !== true) return;
    get().updateUnread(channelId, 0);
  },

  bumpChannelMembersVersion: (channelId) =>
    set((state) => ({
      channelMembersVersion: {
        ...state.channelMembersVersion,
        [channelId]: (state.channelMembersVersion[channelId] ?? 0) + 1,
      },
    })),

  navigateAfterLeave: (channelId) => {
    get().purgeSubtree([channelId]);
    set({ currentChannelId: replacementChannelId(get().channels) });
  },

  purgeSubtree: (channelIds) => {
    const removed = new Set(channelIds);
    set((state) => {
      const channels = state.channels.filter((channel) => !removed.has(channel.id));
      const channelMembersVersion = { ...state.channelMembersVersion };
      for (const id of removed) {
        delete channelMembersVersion[id];
        streamRevisions.delete(id);
      }
      return {
        channels,
        channelMembersVersion,
        currentChannelId:
          state.currentChannelId && removed.has(state.currentChannelId)
            ? replacementChannelId(channels)
            : state.currentChannelId,
      };
    });
    refreshStructure();
  },

  loadChannels: async (guildId) => {
    const targetGuildId = guildId ?? useAuthStore.getState().guildId;
    const scope = targetGuildId ? scopeFor(targetGuildId) : null;
    await Promise.all([
      get().invalidateChannels(guildId),
      scope?.permissionRefreshRequired && targetGuildId === useAuthStore.getState().guildId
        ? refreshPermissions(scope)
        : Promise.resolve(),
    ]);
  },

  invalidateChannels: (guildId) => {
    const targetGuildId = guildId ?? useAuthStore.getState().guildId;
    if (!targetGuildId) {
      cancelReads();
      set({ channels: [], zones: [], loading: false, loadError: null });
      return Promise.resolve();
    }
    const scope = scopeFor(targetGuildId);
    scope.desiredRevision++;
    scope.dirty = true;
    if (targetGuildId !== useAuthStore.getState().guildId) return Promise.resolve();
    if (scope.flight) return scope.flight.promise;

    const generation = contextGeneration;
    const capturedStreamRevision = streamRevision;
    const controller = new AbortController();
    const stillCurrent = () =>
      scopeCurrent(scope) &&
      generation === contextGeneration &&
      targetGuildId === useAuthStore.getState().guildId &&
      !controller.signal.aborted;
    const refresh = async () => {
      try {
        scope.structuralError = null;
        set({ loading: true, loadError: scopeLoadError(scope) });
        while (stillCurrent() && scope.dirty) {
          scope.dirty = false;
          const capturedRevision = scope.desiredRevision;
          try {
            const snapshot = await api.fetchGuildChannels(targetGuildId, controller.signal);
            if (!stillCurrent()) return;
            if (capturedRevision !== scope.desiredRevision) continue;
            validateChannelSnapshot(snapshot, targetGuildId);
            scope.structuralError = null;
            set((state) => ({
              zones: snapshot.zones,
              channels: snapshot.channels.map((channel) => {
                const live = state.channels.find((row) => row.id === channel.id);
                const versions = streamRevisions.get(channel.id);
                return {
                  ...channel,
                  ...(live &&
                  versions?.unread !== undefined &&
                  versions.unread > capturedStreamRevision
                    ? { unread_count: live.unread_count }
                    : {}),
                  ...(live &&
                  versions?.message !== undefined &&
                  versions.message > capturedStreamRevision
                    ? { last_message_at: live.last_message_at }
                    : {}),
                  ...(channel.id === state.currentChannelId && channel.is_member
                    ? { unread_count: 0 }
                    : {}),
                };
              }),
              loadError: scopeLoadError(scope),
            }));
          } catch (error) {
            if (!stillCurrent()) return;
            if (capturedRevision !== scope.desiredRevision) continue;
            scope.structuralError = error;
            set({ loadError: scopeLoadError(scope) });
            if (capturedRevision !== scope.desiredRevision) continue;
            throw error;
          }
        }
      } finally {
        if (scope.flight?.controller === controller) {
          scope.flight = undefined;
          if (stillCurrent()) set({ loading: false });
        }
      }
    };
    let resolve!: () => void;
    let reject!: (error: unknown) => void;
    const promise = new Promise<void>((yes, no) => {
      resolve = yes;
      reject = no;
    });
    scope.flight = { controller, promise };
    void refresh().then(resolve, reject);
    return promise;
  },

  selectChannel: (channelId) => {
    set({ currentChannelId: channelId });
    const channel = get().channels.find((row) => row.id === channelId);
    if (channel?.channel_type === 0 && channel.is_member !== true) return;
    get().clearUnread(channelId);
    api.markChannelRead(channelId).catch(() => {});
  },

  createChannel: async (name, parentId, topic, memberIds, visibility) => {
    const guildId = useAuthStore.getState().guildId;
    const account = accountId();
    const generation = contextGeneration;
    if (!guildId) throw new Error('Select a Project before creating a channel');
    const channel = await api.createChannel(guildId, name, parentId, topic, memberIds, visibility);
    if (
      guildId === useAuthStore.getState().guildId &&
      account === accountId() &&
      generation === contextGeneration
    ) {
      get().addChannel(channel);
      const scope = scopeFor(guildId);
      await Promise.allSettled([scope.flight?.promise, refreshPermissions(scope)]);
    }
    return channel;
  },

  reset: () => {
    cancelReads();
    streamRevisions.clear();
    set({
      channels: [],
      zones: [],
      loading: false,
      loadError: null,
      currentChannelId: null,
      channelMembersVersion: {},
    });
  },
}));
